"use strict";
/**
 * Universal text tokenization utilities
 * Supports all languages including CJK
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isWordLike = isWordLike;
exports.tokenizeText = tokenizeText;
exports.extractWords = extractWords;
exports.countWords = countWords;
exports.splitIntoSentences = splitIntoSentences;
exports.splitIntoSentencesForDictation = splitIntoSentencesForDictation;
const textNormalization_1 = require("./textNormalization");
/**
 * Check if text looks like a word (has letters or CJK characters)
 */
function isWordLike(text) {
    if (!text)
        return false;
    // Contains letters, numbers, or CJK characters
    return /[\p{L}\p{N}\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/u.test(text);
}
/**
 * Tokenize CJK text (character-based for Chinese/Japanese/Korean)
 */
function tokenizeCJKText(text) {
    const tokens = [];
    const _index = 0;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        // CJK characters are treated as individual words
        if (/[\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/.test(char)) {
            tokens.push({
                text: char,
                isWord: true,
                index: i,
            });
        }
        else if (/\s/.test(char)) {
            // Whitespace
            let spaceText = char;
            while (i + 1 < text.length && /\s/.test(text[i + 1])) {
                i++;
                spaceText += text[i];
            }
            tokens.push({
                text: spaceText,
                isWord: false,
                index: i - spaceText.length + 1,
            });
        }
        else {
            // Other characters (punctuation, etc.)
            tokens.push({
                text: char,
                isWord: false,
                index: i,
            });
        }
    }
    return tokens;
}
/**
 * Tokenize non-CJK text (word-based)
 */
function tokenizeLatinText(text) {
    const tokens = [];
    const regex = /\p{L}+|\p{N}+|[^\p{L}\p{N}\s]+|\s+/gu;
    let match;
    while ((match = regex.exec(text)) !== null) {
        const tokenText = match[0];
        tokens.push({
            text: tokenText,
            isWord: isWordLike(tokenText),
            index: match.index,
        });
    }
    return tokens;
}
/**
 * Universal text tokenizer
 * Automatically detects language type and uses appropriate tokenization
 */
function tokenizeText(text, locale = "en") {
    if (!text)
        return [];
    try {
        // Use CJK tokenization for CJK languages
        if ((0, textNormalization_1.containsCJKCharacters)(text) || ["zh", "ja", "ko"].includes(locale)) {
            return tokenizeCJKText(text);
        }
        // Use word-based tokenization for Latin scripts
        return tokenizeLatinText(text);
    }
    catch (error) {
        console.warn("Tokenization error, falling back to simple split:", error);
        // Fallback to simple word splitting
        const words = text.split(/\s+/);
        return words.map((word, index) => ({
            text: word,
            isWord: isWordLike(word),
            index,
        }));
    }
}
/**
 * Extract only word tokens from text
 */
function extractWords(text, locale) {
    return tokenizeText(text, locale)
        .filter((token) => token.isWord)
        .map((token) => token.text);
}
/**
 * Count words in text (supports all languages)
 */
function countWords(text, locale) {
    return extractWords(text, locale).length;
}
/**
 * Split text into sentences
 * Uses language-appropriate sentence boundaries
 */
function splitIntoSentences(text, language) {
    if (!text)
        return [];
    // For CJK languages, use different sentence delimiters
    if (language && ["zh", "ja", "ko"].includes(language)) {
        return text
            .split(/[。！？．]/g)
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
    }
    // For Latin-based languages
    return text
        .split(/[.!?]+\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}
/**
 * Split into sentences specifically for dictation
 * More conservative splitting to avoid very short segments
 */
function splitIntoSentencesForDictation(text, language) {
    const sentences = splitIntoSentences(text, language);
    const result = [];
    for (let i = 0; i < sentences.length; i++) {
        const sentence = sentences[i];
        const wordCount = countWords(sentence, language);
        // If sentence is very short (< 3 words), try to combine with next
        if (wordCount < 3 && i < sentences.length - 1) {
            const nextSentence = sentences[i + 1];
            const combinedWordCount = countWords(sentence + " " + nextSentence, language);
            // Combine if total is reasonable (< 15 words)
            if (combinedWordCount <= 15) {
                result.push(sentence + ". " + nextSentence);
                i++; // Skip next sentence as it's already combined
                continue;
            }
        }
        result.push(sentence);
    }
    return result;
}
//# sourceMappingURL=textTokenizer.js.map